import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getTeamData,
    getTeamGear,
    getWeekStatus,
    equipGearToCharacterSheet,
    unequipGear,
} from '../DatabaseAPI';
import { Button } from '../components/ui';
import CollapsibleScoreBreakdown from '../components/player/CollapsibleScoreBreakdown';
import PlayerGearProjectionTab from '../components/player/PlayerGearProjectionTab';
import { buildProjectionSummary } from '../utils/playerProjection';
import { formatPoints } from '../utils/statLineFormat';
import { resolveMediaUrl } from '../api/transport';
import './PlayerPage.css';

/**
 * Is this week-status payload TRUSTWORTHY ENOUGH TO GATE GEAR WRITES ON?
 *
 * ⚠️ A RESOLVED PROMISE IS NOT A SUCCESSFUL READ. `getWeekStatus()` in
 * `api/leagueApi.js` does NOT reject on failure:
 *   • network/parse failure → its `.catch` RESOLVES `null`;
 *   • a 401/403/500 → `response.json()` resolves the ERROR BODY
 *     (e.g. `{ detail: 'Authentication credentials were not provided.' }`),
 *     because nothing checks `response.ok`.
 *
 * Both outcomes land in the success branch of the caller's `try`, so a
 * try/catch alone marks the status "resolved". Neither value has a
 * `stats_active` field, so `!weekStatusState?.stats_active` is `true` and the
 * page would conclude the week is OPEN — enabling gear writes precisely when
 * the server is unreachable or the session has expired.
 *
 * That is the exact fail-OPEN this page's gating exists to prevent, and the
 * weekly backend has no scoring freeze to catch it. So we demand positive
 * proof: an actual boolean `stats_active`. Anything else is UNKNOWN, and
 * unknown denies.
 */
function isUsableWeekStatus(status) {
    return Boolean(status) && typeof status.stats_active === 'boolean';
}

function PlayerPage({
    playerId,
    teamId,
    leagueId,
    onNavigate,
    // Aliased deliberately. The body must never confuse the PROP (an optional
    // hint from a host that already knows the week) with `weekStatusState` (the
    // value this page resolves for itself and gates gear writes on). The prop
    // is only ever read as a "can I skip the fetch?" seed — see the week-status
    // state declaration below for why absence must NOT mean "writable".
    weekStatus: propWeekStatus = null,
    viewMode = 'own'
}) {
    const [playerData, setPlayerData] = useState(null);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeBottomButton, setActiveBottomButton] = useState(null); // null, 'pts', 'gear'
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // ===== GEAR TAB STATE =====
    // The gear tab edits the loadout in place, so PlayerPage owns the team's
    // full gear inventory (not just this player's equipment map).
    //
    // ⚠️ THE DRAFT-POOL RANK LOAD IS GONE, DELIBERATELY. This page used to
    // fetch `position_rank` for all three positions to resolve a tier in JS.
    // The projection endpoint now resolves the rank ITSELF, from the SAME
    // `WeeklyPlayerPool` rows the draft pool reads
    // (`ProjectedGearScoringService._resolve_position_rank`). Keeping the
    // client fetch would mean three extra round trips per player view whose
    // result nothing reads — and, worse, a SECOND opinion about which tier the
    // player is in, free to disagree with the tier the engine actually scored.
    // One resolver, server-side, is the point.
    //
    // `resolvedTeamId` is captured during load so the equip/unequip handlers do
    // not have to re-derive it from props/sessionStorage on every call.
    const [teamGear, setTeamGear] = useState([]);
    const [resolvedTeamId, setResolvedTeamId] = useState(null);

    // Why this is tracked SEPARATELY from `teamGear` rather than inferred from
    // an empty array: "this team owns no gear" and "we could not read this
    // team's gear" are different facts that must never render the same way.
    // An unreadable inventory shown as an empty one would tell a manager their
    // loadout is bare during an outage, hiding gear they actually have equipped.
    // `null` = the read succeeded; an Error = it failed and the tab must say so.
    const [gearLoadError, setGearLoadError] = useState(null);

    // ===== WEEK STATUS — GATES EVERY GEAR MUTATION =====
    //
    // ⚠️ THIS PAGE MUST FETCH ITS OWN STATUS AND FAIL CLOSED. Do not reintroduce
    // "no status means writable".
    //
    // The `weekStatus` PROP IS NOT SUPPLIED BY THE PRODUCTION ROUTE. AppRouter's
    // `playerPage` case mounts PlayerPage with playerId/teamId/leagueId/
    // onNavigate/viewMode and no weekStatus, so every player page reached
    // through that host saw `weekStatus === null`. The gear tab previously read
    // `weekStatus ? !weekStatus.stats_active : true`, which turned that absence
    // into FULL WRITE PERMISSION — including while a weekly league was actively
    // scoring.
    //
    // THE SERVER DOES NOT CATCH THIS FOR WEEKLY LEAGUES. `GearEquipService`
    // enforces the freeze via `lock_governing_week_for_team()` and
    // `SeasonMutationGuard`, and BOTH deliberately no-op on weekly leagues
    // (`if team.league.mode != 'SEASON': return None` /
    // `if league.mode != 'SEASON': return`). There is no `stats_active` check
    // anywhere in the equip path. So a weekly loadout really could be changed
    // mid-scoring — this was a data-integrity bug, not a cosmetic one.
    //
    // `null` here means UNKNOWN, not "open": controls stay disabled until the
    // read succeeds. Blocking a legitimate edit for a moment is recoverable;
    // silently mutating a scoring lineup is not.
    // Even the PROP must prove itself — a host passing a malformed/failed
    // status must not seed this page into a "resolved" state.
    const [weekStatusState, setWeekStatusState] = useState(
        isUsableWeekStatus(propWeekStatus) ? propWeekStatus : null
    );
    const [weekStatusResolved, setWeekStatusResolved] = useState(
        isUsableWeekStatus(propWeekStatus)
    );

    /**
     * THE SINGLE GEAR-WRITE PREDICATE. Both the UI gate and the handler gates
     * read this one expression so they cannot drift apart.
     *
     * Requires BOTH facts to be true:
     *   1. the status was actually read  (`weekStatusResolved`), and
     *   2. it says the week is not scoring (`!stats_active`).
     *
     * Unknown therefore denies. That asymmetry is deliberate: a wrongly-blocked
     * edit is visible and recoverable by reloading, while a wrongly-allowed one
     * silently corrupts a lineup that is already being scored.
     */
    const gearMutationsPermitted = weekStatusResolved && !weekStatusState?.stats_active;

    // ===== VIEW MODE =====
    // MUST be declared BEFORE `pageProjection` below, which reads
    // `isOpponentView` as a useMemo dependency. These are plain `const`
    // bindings, not hooks, so they are NOT hoisted the way a function
    // declaration is — a reference above this line hits the temporal dead zone
    // and throws "Cannot access 'isOpponentView' before initialization" on
    // first render, which crashes the whole page rather than degrading.
    //
    // `viewMode` prop wins; sessionStorage is the fallback for hosts that
    // navigate here without passing it.
    const effectiveViewMode = viewMode || sessionStorage.getItem('playerViewMode') || 'own';
    const isOpponentView = effectiveViewMode === 'opponent';

    /**
     * THIS PLAYER'S EQUIPPED LOADOUT, derived from the team inventory.
     *
     * ⚠️ `character_sheet_id` ONLY — no fallback to `playerData.id`. That is a
     * LeaguePlayer PK, a different table from CharacterSheet, and the two
     * sequences overlap: falling back would silently attribute ANOTHER
     * player's gear to this one and project a loadout this player does not
     * have. An unknown sheet id means we cannot attribute gear at all, so the
     * projection is requested with NO gear rather than with somebody else's —
     * matching the gear tab, which refuses to render its editor in the same
     * situation.
     *
     * Memoised on identity so the projection effect below does not re-fire on
     * every unrelated render; `teamGear` only changes when the server was
     * actually re-read.
     */
    const heldGear = useMemo(() => {
        const characterSheetId = playerData?.character_sheet_id ?? null;
        if (characterSheetId === null) return [];

        return (teamGear || []).filter(
            (item) => item.character_sheet != null
                && item.character_sheet === characterSheetId
        );
    }, [playerData, teamGear]);

    /**
     * ONE PROJECTION FOR THE WHOLE SCREEN — fetched ONCE, SHARED BY BOTH
     * SURFACES.
     *
     * ⚠️ THE SCORE BAR AND THE GEAR TAB RENDER THE SAME OBJECT. They do not
     * each call the adapter and compare results — the tab receives this
     * summary as a prop. That is stronger than "they agree by construction":
     * there is only one construction. A second call could diverge from this
     * one across a cache boundary or a re-equip race, and the whole point of
     * the feature is that the always-visible `PROJ` figure and the `Total:`
     * two inches above it are never seen disagreeing.
     *
     * ⚠️ THE SCORE BAR MUST NOT SHOW `playerData.projected_points`. That field
     * comes from `NFLProjectionMirror` — a DIFFERENT provider. Rendering it
     * put two competing "projections" on one screen (`PROJ: 18.7` at the
     * bottom, `Base: 13.65` in the tab) with nothing to tell the user which to
     * believe. `Player-Gear-Projections.md` §14 puts that field explicitly out
     * of scope and §10 requires every surface to read the one adapter.
     *
     * WHY THIS IS NOW AN EFFECT AND NOT A `useMemo`. The adapter awaits the
     * server's engine run, so the value cannot be produced during render. The
     * three states are tracked explicitly and are NOT interchangeable:
     *   • `null` summary + `pending: true`  → still loading. Renders "…", not
     *     a projection and not an error.
     *   • `null` summary + an `error`       → the read FAILED. Must never
     *     render as "no projection data"; that is the honest-absence state,
     *     which the server reports as `available: false` instead.
     *   • a summary object                  → render it.
     */
    const [pageProjection, setPageProjection] = useState(null);
    const [projectionPending, setProjectionPending] = useState(true);
    const [projectionError, setProjectionError] = useState(null);

    useEffect(() => {
        // ⚠️ AN UNREADABLE INVENTORY IS NOT AN EMPTY ONE — AND PROJ MUST NOT
        // PRETEND OTHERWISE. The load effect clears `teamGear` to `[]` when
        // `getTeamGear()` fails, precisely so no stale loadout lingers behind
        // the tab's error panel. Projecting that empty array would ask the
        // server for an explicitly-empty loadout and publish the answer as a
        // confident total — one screen claiming "your loadout is unknown"
        // directly above "PROJ: 21.34", with the precise number being the more
        // believable of the two. The em dash is the honest answer.
        if (!playerData || gearLoadError) {
            setPageProjection(null);
            setProjectionPending(false);
            return undefined;
        }

        const projectionTeamId =
            resolvedTeamId || teamId || sessionStorage.getItem('selectedTeamId');

        if (!projectionTeamId) {
            setPageProjection(null);
            setProjectionPending(false);
            return undefined;
        }

        // ⚠️ IGNORE A RESPONSE THAT ARRIVES AFTER WE MOVED ON. Switching
        // players or equipping twice quickly leaves an earlier request in
        // flight; without this guard its late response overwrites the current
        // one and the screen shows the PREVIOUS loadout's projection against
        // the CURRENT loadout's gear list — a wrong number that looks entirely
        // plausible.
        let cancelled = false;

        setProjectionPending(true);
        setProjectionError(null);

        buildProjectionSummary({
            player: playerData,
            teamId: projectionTeamId,
            equippedGear: heldGear,
            isOpponentView,
        })
            .then((summary) => {
                if (cancelled) return;
                setPageProjection(summary);
                setProjectionError(null);
            })
            .catch((error) => {
                if (cancelled) return;
                // A FAILED READ IS NOT "NO PROJECTION". It is surfaced as its
                // own state so neither surface can render it as the honest
                // "this player has no tier data" panel — those are different
                // facts and must not look the same.
                console.error(
                    '⚠️ PlayerPage: the gear projection could not be read; both the ' +
                    'PROJ bar and the GEAR tab will say so explicitly rather than ' +
                    'showing a projection that excludes gear.',
                    error
                );
                setPageProjection(null);
                setProjectionError(error);
            })
            .finally(() => {
                if (cancelled) return;
                setProjectionPending(false);
            });

        return () => {
            cancelled = true;
        };
    }, [playerData, heldGear, isOpponentView, gearLoadError, resolvedTeamId, teamId]);

    // Helper function to sort players by position (QB, RB, WR) - MUST match TeamPage and GearPage
    const sortPlayersByPosition = (players) => {
        const positionOrder = { 'QB': 1, 'RB': 2, 'WR': 3 };
        
        return [...players].sort((a, b) => {
            const orderA = positionOrder[a.position] || 999;
            const orderB = positionOrder[b.position] || 999;
            return orderA - orderB;
        });
    };

    // Load player data
    useEffect(() => {
        const loadPlayerData = async () => {
            try {
                setLoading(true);
                
                // Recalculate viewMode inside effect to ensure it's current
                const currentViewMode = viewMode || sessionStorage.getItem('playerViewMode') || 'own';
                const currentIsOpponentView = currentViewMode === 'opponent';
                
                const effectivePlayerId = playerId || sessionStorage.getItem('selectedPlayerId');
                // When in opponent view, use opponentTeamId instead of selectedTeamId
                let effectiveTeamId = teamId;
                if (!effectiveTeamId) {
                    if (currentIsOpponentView) {
                        effectiveTeamId = sessionStorage.getItem('opponentTeamId');
                    } else {
                        effectiveTeamId = sessionStorage.getItem('selectedTeamId');
                    }
                }
                
                console.log('PlayerPage: Loading with viewMode:', currentViewMode, 'isOpponent:', currentIsOpponentView);
                console.log('PlayerPage: Using teamId:', effectiveTeamId, 'playerId:', effectivePlayerId);
                
                if (!effectivePlayerId) {
                    throw new Error('No player ID provided');
                }
                
                if (!effectiveTeamId) {
                    throw new Error('No team ID provided');
                }

                const teamData = await getTeamData(effectiveTeamId);
                
                if (!teamData || !teamData.players) {
                    throw new Error('Failed to load team data');
                }

                // Sort players by position to match TeamPage and GearPage order
                const sortedPlayers = sortPlayersByPosition(teamData.players);
                
                // Store all team players for navigation
                setTeamPlayers(sortedPlayers);

                console.log('PlayerPage: Team has', sortedPlayers.length, 'players');
                console.log('PlayerPage: Looking for player ID:', effectivePlayerId);
                console.log('PlayerPage: Player IDs in team:', sortedPlayers.map(p => ({
                    id: p.id,
                    character_sheet_id: p.character_sheet_id,
                    name: p.name
                })));

                const playerIndex = sortedPlayers.findIndex(p =>
                    p.id === parseInt(effectivePlayerId) ||
                    p.character_sheet_id === parseInt(effectivePlayerId)
                );

                if (playerIndex === -1) {
                    console.error('PlayerPage: Player not found! Searched for:', effectivePlayerId);
                    console.error('PlayerPage: Available players:', sortedPlayers.map(p => `${p.name} (id:${p.id}, cs_id:${p.character_sheet_id})`));
                    throw new Error('Player not found in team');
                }

                setCurrentPlayerIndex(playerIndex);
                const player = sortedPlayers[playerIndex];
                setPlayerData(player);
                setResolvedTeamId(effectiveTeamId);

                // ⭐ THE PAGE IS RENDERABLE NOW — STOP BLOCKING ON THE REST.
                //
                // Everything above this line is what the player SHEET needs:
                // the image, name, position, score and PTS breakdown all come
                // from `getTeamData()`. The three reads below (gear, ranks,
                // week status) feed the GEAR tab and the PROJ figure only.
                //
                // They used to run sequentially BEFORE `setLoading(false)`, so
                // the user stared at "Loading Player Data..." for the sum of
                // four round trips — and a single slow auxiliary call held the
                // entire sheet hostage even though none of its primary content
                // depended on that call. On a slow connection the page could
                // sit blank for seconds with the data to draw it already in
                // hand.
                //
                // Releasing here means the sheet paints as soon as the player
                // resolves. Each consumer already has an honest pending/absent
                // state: PROJ shows the em dash until ranks arrive, the gear
                // tab renders "Loading projection data…", and gear writes stay
                // FAIL-CLOSED until the week status is positively confirmed —
                // so an unresolved read can never be mistaken for permission.
                setLoading(false);

                // NOTE: the old per-player `getPlayerEquipment()` call was
                // dropped here. Its `{ SLOT: gear }` map existed solely to feed
                // the read-only gear list this tab replaced, and the new tab
                // derives the loadout from the team gear inventory below —
                // where `character_sheet` is the authoritative owner field.
                // Keeping both would mean two sources of truth for one loadout,
                // free to disagree after an equip.

                // ===== AUXILIARY DATA (GEAR TAB + PROJ) =====
                // Loaded alongside the player so the GEAR tab is ready the
                // instant it is opened, rather than flashing empty.
                //
                // ⚠️ RUN CONCURRENTLY, NOT IN SEQUENCE. These three reads are
                // mutually independent — none consumes another's result — so
                // awaiting them one after another merely added their latencies
                // together for no benefit. Started together here and awaited as
                // a group below, the tab is ready after the SLOWEST of the
                // three rather than the sum.
                //
                // NONE OF THEM MAY BLOCK OR BREAK THE PAGE. The sheet's core
                // purpose (image, details, points) does not depend on any of
                // them, and `setLoading(false)` has already run. Each owns its
                // own try/catch, so one failing neither aborts the others nor
                // reaches the outer handler and turns the whole screen into an
                // error state — it degrades only the surface it feeds.
                //
                // ⚠️ A FAILED GEAR READ IS NOT AN EMPTY INVENTORY. `getTeamGear()`
                // rejects on failure instead of resolving to `[]`, so the catch
                // below is reachable and the tab is told explicitly that the
                // loadout is UNKNOWN. Rendering it as empty would claim the
                // manager has nothing equipped while their real gear is simply
                // unreadable.
                const gearLoad = (async () => {
                    try {
                        const gear = await getTeamGear(effectiveTeamId);
                        setTeamGear(gear || []);
                        setGearLoadError(null);
                    } catch (gearError) {
                        console.error(
                            '⚠️ PlayerPage: could not load team gear; the GEAR tab will ' +
                            'show an explicit error rather than a misleading empty inventory.',
                            gearError
                        );
                        // Cleared so no stale loadout lingers behind the error panel.
                        setTeamGear([]);
                        setGearLoadError(gearError);
                    }
                })();

                // RANKS ARE OWNER-ONLY — DO NOT REQUEST THEM FOR AN OPPONENT.
                //
                // `position_rank` comes from the draft-pool endpoint, and
                // `DraftPoolPlayersView` rejects any team the requester does not
                // own with a 403 (see draft_pool_views.py, "Access denied — team
                // belongs to another user"). In opponent view `effectiveTeamId`
                // IS the opponent's team, so all three position requests would
                // fail by design. Firing them anyway would mean three guaranteed
                // 403s per player view, noisy console errors on a perfectly
                // normal screen, and an identical end state to not asking.
                //
                // WHY WE DON'T BORROW THE VIEWER'S OWN POOL AS A STAND-IN:
                // `position_rank` lives on `WeeklyPlayerPool` rows filtered by
                // `draft_catalog`, and `current_draft_catalog` is a per-TEAM
                // FK assigned from `DraftCatalog.objects.first()` when the team
                // is created — there is no league-level invariant forcing two
                // teams in the same league onto the same catalog. Two managers
                // who joined either side of a catalog change can legitimately
                // hold different pools, so a rank read from OUR pool could
                // silently misreport THEIR player's tier. An honest
                // "unavailable" beats a confident wrong number, and inventing a
                // new backend rank contract is out of scope for this V1.
                // ===== WEEK STATUS — FETCHED HERE, NOT ASSUMED =====
                // Same source GearPage uses (`getWeekStatus(leagueId)` →
                // `/v2/leagues/<id>/stats/`), so both screens agree on whether
                // the week is scoring. Only skipped when the prop already
                // supplied it (the MythicFootball shell does pass it).
                //
                // ON FAILURE WE STAY LOCKED. `weekStatusResolved` remains false,
                // so `canModifyGear` is false and the tab explains that the
                // status is unknown. This is the deliberate inverse of the gear
                // and rank reads above, which degrade toward "show less": here
                // the safe direction is to WITHHOLD WRITES, because the backend
                // has no weekly freeze of its own to fall back on.
                // ⚠️ VALIDATE THE PAYLOAD — A RESOLVED PROMISE IS NOT A
                // SUCCESSFUL READ. `getWeekStatus()` resolves `null` on network
                // failure and resolves the ERROR BODY on a 401/500 (it never
                // checks `response.ok`), so the `catch` below is NOT reachable
                // for the most likely outage modes. Trusting resolution alone
                // would mark a failed read "resolved" with no `stats_active`,
                // which reads as "week open" and ENABLES writes during an
                // outage. `isUsableWeekStatus()` demands a real boolean.
                const weekStatusLoad = (async () => {
                    if (isUsableWeekStatus(propWeekStatus)) return;

                    const effectiveLeagueId =
                        leagueId || sessionStorage.getItem('selectedLeagueId');

                    if (!effectiveLeagueId) {
                        console.error(
                            '⚠️ PlayerPage: no league id available to read week status; ' +
                            'gear modification stays DISABLED (fail closed).'
                        );
                        setWeekStatusState(null);
                        setWeekStatusResolved(false);
                        return;
                    }

                    try {
                        const status = await getWeekStatus(effectiveLeagueId);

                        if (isUsableWeekStatus(status)) {
                            setWeekStatusState(status);
                            setWeekStatusResolved(true);
                        } else {
                            console.error(
                                '⚠️ PlayerPage: week status response carried no ' +
                                'boolean `stats_active`, so the week is UNKNOWN and ' +
                                'gear modification stays DISABLED (fail closed). ' +
                                'This is the shape returned by a failed/unauthorized ' +
                                'read, which resolves rather than rejecting.',
                                status
                            );
                            setWeekStatusState(null);
                            setWeekStatusResolved(false);
                        }
                    } catch (weekStatusError) {
                        console.error(
                            '⚠️ PlayerPage: could not read week status; gear ' +
                            'modification stays DISABLED (fail closed) because a ' +
                            'weekly league has no server-side scoring freeze.',
                            weekStatusError
                        );
                        setWeekStatusState(null);
                        setWeekStatusResolved(false);
                    }
                })();

                // ⚠️ NO RANK LOAD HERE ANY MORE — see the state declaration at
                // the top of this component. The projection endpoint resolves
                // `position_rank` server-side from the same pool rows, so the
                // three owner-only `/draft-pool/` requests this page used to
                // fire are gone. In opponent view that was three guaranteed
                // 403s per player view; now it is zero requests either way.

                // Neither of these rejects — each handles its own failure above
                // — so `all` is safe here and simply marks the point where the
                // auxiliary data has settled. The page is ALREADY interactive;
                // this only keeps the async function alive until the work is
                // done, so nothing is left dangling.
                await Promise.all([gearLoad, weekStatusLoad]);
            } catch (err) {
                console.error('Error loading player data:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        loadPlayerData();
    }, [playerId, teamId, viewMode]);

    // Prevent body and html scrolling on mobile when PlayerPage is mounted
    useEffect(() => {
        // Save original styles
        const originalBodyOverflow = document.body.style.overflow;
        const originalBodyPosition = document.body.style.position;
        const originalBodyWidth = document.body.style.width;
        const originalBodyTop = document.body.style.top;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        
        // Force scroll to top immediately
        window.scrollTo(0, 0);
        
        // Prevent scrolling on both html and body
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = '0';
        document.body.style.width = '100%';
        document.body.style.left = '0';
        document.body.style.right = '0';
        
        // Add scroll event listener to force page to stay at top
        const preventScroll = (e) => {
            window.scrollTo(0, 0);
            e.preventDefault();
        };
        
        // Prevent touchmove but allow scrolling in content areas
        const preventTouchmove = (e) => {
            // Allow scrolling inside the points breakdown, details, or gear content
            if (e.target.closest('.points-breakdown') ||
                e.target.closest('.details-content') ||
                e.target.closest('.gear-content')) {
                return; // Don't prevent - allow native scrolling
            }
            e.preventDefault();
        };
        
        window.addEventListener('scroll', preventScroll, { passive: false });
        document.addEventListener('touchmove', preventTouchmove, { passive: false });
        
        // Restore on unmount
        return () => {
            window.removeEventListener('scroll', preventScroll);
            document.removeEventListener('touchmove', preventTouchmove);
            
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.position = originalBodyPosition;
            document.body.style.top = originalBodyTop;
            document.body.style.width = originalBodyWidth;
            document.body.style.left = '';
            document.body.style.right = '';
        };
    }, []);

    const getPlayerImagePath = (player) => {
        // Prefer backend-resolved presentation URL (equipped variant or default art gateway).
        // resolveMediaUrl converts the relative /api/... path to an absolute backend URL
        // in dev (where the React dev server and Django run on different ports).
        if (player.presentation?.image_url) {
            return resolveMediaUrl(player.presentation.image_url);
        }
        const gsisId = player.gsis_id || player.source_player_id;
        
        if (!gsisId) {
            console.error('No GSIS ID found for player:', player.name);
            return '/art/player_images_gsis_cartoon/default.png';
        }
        
        return `/art/player_images_gsis_cartoon/${gsisId}.png`;
    };

    const handlePlayerImageError = (event, player) => {
        const image = event.currentTarget;
        const gsisId = player.gsis_id || player.source_player_id;
        const playerIdentity = String(player.character_sheet_id || player.id || gsisId || player.name);
        const hasPresentationImage = Boolean(player.presentation?.image_url);
        const gsisFallback = gsisId ? `/art/player_images_gsis_cartoon/${gsisId}.png` : null;
        const defaultFallback = '/art/player_images_gsis_cartoon/default.png';

        if (image.dataset.fallbackPlayer !== playerIdentity) {
            image.dataset.fallbackPlayer = playerIdentity;
            image.dataset.fallbackStage = hasPresentationImage
                ? 'presentation'
                : (gsisFallback ? 'gsis' : 'default');
        }

        if (image.dataset.fallbackStage === 'presentation') {
            image.dataset.fallbackStage = gsisFallback ? 'gsis' : 'default';
            image.src = gsisFallback || defaultFallback;
            return;
        }

        if (image.dataset.fallbackStage === 'gsis') {
            image.dataset.fallbackStage = 'default';
            image.src = defaultFallback;
            return;
        }

        image.dataset.fallbackStage = 'stopped';
        image.onerror = null;
    };

    // NAVIGATION VOCABULARY — PlayerPage emits ONE canonical uppercase destination
    // set and lets whichever host mounted it do the mapping. It deliberately does
    // NOT try to work out which router it is running under.
    //
    // WHY (2026-09-20 bug): it used to sniff `sessionStorage.selectedTeamId` and
    // treat a non-null value as "I'm mounted by AppRouter", emitting AppRouter's
    // lowercase app-mode strings ('gearPage', 'teamPage', ...) in that case and
    // the MythicFootball/NewApp shell's uppercase strings otherwise. That test is
    // simply wrong, because the two facts are not exclusive: the post-draft NewApp
    // shell is reached FROM AppRouter, which has already written `selectedTeamId`
    // in handleTeamClick. So inside the shell PlayerPage mis-identified its host,
    // sent 'gearPage' into NewApp.handleTeamPageNavigation, hit that handler's
    // default branch ("Unknown navigation destination in NewApp: gearPage") and
    // the GEAR / EQUIP buttons did nothing at all on the first post-draft visit.
    //
    // Both hosts now speak this vocabulary, so nothing has to be detected:
    //   'LEAGUES' | 'TEAM' | 'STANDINGS' | 'GEAR'
    // plus 'HOME', which the error / no-data screens below emit directly for
    // "get me out of here" recovery.
    const handleNavigation = (destination) => {
        console.log('PlayerPage handleNavigation called with:', destination);

        switch(destination) {
            case 'LEAGUES':
                // Leaving this player entirely — drop the selection so the next
                // team we open does not re-open this player's sheet.
                sessionStorage.removeItem('selectedPlayerId');
                onNavigate('LEAGUES');
                break;
            case 'TEAM':
            case 'STANDINGS':
                onNavigate(destination);
                break;
            case 'GEAR':
                // Gear is read-only in an opponent view: there is nothing for the
                // manager to equip on someone else's roster.
                if (!isOpponentView) {
                    onNavigate('GEAR');
                }
                break;
            default:
                throw new Error(
                    `Unknown navigation destination in PlayerPage: ${JSON.stringify(destination)}. ` +
                    "Expected one of 'LEAGUES', 'TEAM', 'STANDINGS', 'GEAR'."
                );
        }
    };

    /**
     * Refresh the team's gear inventory from the server.
     *
     * ALWAYS RE-READ RATHER THAN PATCHING LOCAL STATE. An equip can have
     * server-side side effects beyond the one row the UI touched (slot
     * displacement, season mutation guards rejecting the write), so the server
     * response is the only trustworthy picture of the loadout. Re-reading also
     * means a rejected write simply leaves the UI showing reality.
     */
    const refreshTeamGear = useCallback(async () => {
        const teamIdForGear =
            resolvedTeamId || teamId || sessionStorage.getItem('selectedTeamId');

        if (!teamIdForGear) {
            throw new Error(
                'PlayerPage.refreshTeamGear() could not resolve a team id. Expected ' +
                'one from the page load, the teamId prop, or sessionStorage.'
            );
        }

        // A failed re-read after a successful write is the most dangerous case
        // of all: the server state HAS changed, so continuing to display the
        // pre-write list would be actively wrong. Surface the error and clear
        // the stale list rather than leaving a confidently incorrect loadout on
        // screen. The rejection still propagates so the calling handler can
        // report it too.
        try {
            const gear = await getTeamGear(teamIdForGear);
            setTeamGear(gear || []);
            setGearLoadError(null);
        } catch (refreshError) {
            console.error(
                '⚠️ PlayerPage: gear inventory could not be re-read after a ' +
                'modification; the displayed loadout is now UNKNOWN, not empty.',
                refreshError
            );
            setTeamGear([]);
            setGearLoadError(refreshError);
            throw refreshError;
        }
    }, [resolvedTeamId, teamId]);

    /**
     * RE-CONFIRM, AT THE MOMENT OF THE WRITE, THAT THE WEEK IS STILL OPEN.
     *
     * ⚠️ WHY A SECOND CHECK EXISTS AT ALL. `gearMutationsPermitted` is a
     * SNAPSHOT taken during page load. A player sheet is a screen people leave
     * open — on a phone, in a background tab, while watching the games start.
     * When the league flips to `stats_active: true` in that window, the cached
     * predicate stays `true` forever, because nothing re-reads it. Both
     * handlers would keep accepting writes into a week that is actively being
     * scored.
     *
     * THERE IS NO SERVER-SIDE NET FOR THIS ON WEEKLY LEAGUES.
     * `GearEquipService` enforces the freeze via `lock_governing_week_for_team()`
     * and `SeasonMutationGuard`, and BOTH deliberately no-op when
     * `league.mode != 'SEASON'`. The equip path contains no `stats_active`
     * check of its own. So for a weekly league this client-side re-read is not
     * belt-and-braces over a server guard — it is the ONLY guard.
     *
     * FAILS CLOSED, INCLUDING ON THE HELPER'S QUIRKS. `getWeekStatus()`
     * resolves `null` on network failure and resolves the ERROR BODY on a
     * 401/500 (it never checks `response.ok`), so "it returned something" is
     * not evidence of success. `isUsableWeekStatus()` demands a real boolean,
     * and anything else denies the write. Blocking a legitimate edit is
     * recoverable by reloading; corrupting a lineup mid-scoring is not.
     *
     * @returns {Promise<boolean>} true only when the week is CONFIRMED writable.
     */
    const confirmWeekStillWritable = useCallback(async () => {
        const effectiveLeagueId =
            leagueId || sessionStorage.getItem('selectedLeagueId');

        if (!effectiveLeagueId) {
            console.error(
                '⛔ PlayerPage: no league id available to re-confirm week status ' +
                'immediately before a gear write; refusing the write (fail closed).'
            );
            return false;
        }

        let status;
        try {
            status = await getWeekStatus(effectiveLeagueId);
        } catch (statusError) {
            console.error(
                '⛔ PlayerPage: could not re-confirm week status immediately before ' +
                'a gear write; refusing the write (fail closed).',
                statusError
            );
            return false;
        }

        if (!isUsableWeekStatus(status)) {
            console.error(
                '⛔ PlayerPage: pre-write week status carried no boolean ' +
                '`stats_active`, so the week is UNKNOWN; refusing the write ' +
                '(fail closed). This is the shape a failed/unauthorized read ' +
                'resolves with rather than rejecting.',
                status
            );
            return false;
        }

        // Keep the rendered gate in step with what we just learned, so the UI
        // stops offering writes it will now refuse. Without this the cards stay
        // enabled and every subsequent click fails silently from the user's
        // point of view.
        setWeekStatusState(status);
        setWeekStatusResolved(true);

        if (status.stats_active) {
            console.error(
                '⛔ PlayerPage: the week began scoring while this page was open. ' +
                'Refusing the gear write — a weekly league has no server-side ' +
                'freeze to catch it.'
            );
            return false;
        }

        return true;
    }, [leagueId]);

    /**
     * Equip one item to a character sheet, then re-sync.
     *
     * The explicit character-sheet guard mirrors GearPage's: `/v2/gear/<id>/equip/`
     * requires a real CharacterSheet PK, and posting undefined would 400 with an
     * opaque serializer error while substituting a different id could equip onto
     * the WRONG player's sheet. Reaching here without one means the payload is
     * malformed, so fail loudly rather than guess.
     */
    const handleEquipGear = useCallback(async (gearId, characterSheetId) => {
        // DEFENCE IN DEPTH — this handler is the LAST client-side gate before a
        // real mutation, and for a weekly league there is no server-side gate
        // behind it at all (SeasonMutationGuard no-ops when mode != 'SEASON').
        // The button is already disabled, but a stale render, a race against the
        // status read, or a future caller that forgets to check must not be able
        // to slip a write through into a scoring week.
        if (!gearMutationsPermitted) {
            console.error(
                '⛔ PlayerPage: refusing to equip gear — the week is scoring or its ' +
                'status could not be confirmed. Weekly leagues have no server-side ' +
                'scoring freeze, so this check is the one that matters.',
                { gearId, weekStatusResolved, statsActive: weekStatusState?.stats_active }
            );
            return;
        }

        // The snapshot above can be arbitrarily old — see
        // `confirmWeekStillWritable()`. Re-read before touching the lineup.
        if (!await confirmWeekStillWritable()) {
            alert(
                'Gear cannot be changed right now — the scoring week has started ' +
                'or its status could not be confirmed. Reload to see the current state.'
            );
            return;
        }

        if (characterSheetId === undefined || characterSheetId === null) {
            const message =
                'Cannot equip: this player has no character sheet id. Expected the ' +
                'roster payload to carry character_sheet_id.';
            console.error(`❌ PlayerPage: ${message}`, { gearId });
            alert(message);
            return;
        }

        try {
            await equipGearToCharacterSheet(gearId, characterSheetId);
            await refreshTeamGear();
        } catch (equipError) {
            console.error('Error equipping gear:', equipError);
            alert('Failed to equip gear: ' + (equipError.message || equipError));
        }
    }, [
        refreshTeamGear,
        gearMutationsPermitted,
        weekStatusResolved,
        weekStatusState,
        confirmWeekStillWritable,
    ]);

    /** Unequip one item, then re-sync. */
    const handleUnequipGear = useCallback(async (gearId) => {
        // Same last-gate reasoning as handleEquipGear — stripping gear from a
        // live lineup corrupts it exactly as adding gear does.
        if (!gearMutationsPermitted) {
            console.error(
                '⛔ PlayerPage: refusing to unequip gear — the week is scoring or its ' +
                'status could not be confirmed.',
                { gearId, weekStatusResolved, statsActive: weekStatusState?.stats_active }
            );
            return;
        }

        // REMOVING gear from a scoring lineup corrupts it exactly as adding
        // does, so the same fresh re-read gates both directions.
        if (!await confirmWeekStillWritable()) {
            alert(
                'Gear cannot be changed right now — the scoring week has started ' +
                'or its status could not be confirmed. Reload to see the current state.'
            );
            return;
        }

        try {
            await unequipGear(gearId);
            await refreshTeamGear();
        } catch (unequipError) {
            console.error('Error unequipping gear:', unequipError);
            alert('Failed to unequip gear: ' + (unequipError.message || unequipError));
        }
    }, [
        refreshTeamGear,
        gearMutationsPermitted,
        weekStatusResolved,
        weekStatusState,
        confirmWeekStillWritable,
    ]);

    const handleBottomButtonClick = (button) => {
        if (activeBottomButton === button) {
            setActiveBottomButton(null);
        } else {
            setActiveBottomButton(button);
        }
    };

    /**
     * Switch to another player on the roster (dots / swipe).
     *
     * NO GEAR REFETCH IS NEEDED. `teamGear` is TEAM-WIDE, not per-player — the
     * gear tab filters the inventory by the selected player's
     * `character_sheet_id`, so switching only changes which slice is shown.
     *
     * The PROJECTION is per-player and does refresh, but not from here: the
     * projection effect depends on `playerData`, so selecting a new player
     * re-runs it automatically and cancels any in-flight request for the
     * previous one. This handler performs no I/O itself.
     */
    const handlePlayerSwitch = (index) => {
        if (index === currentPlayerIndex || !teamPlayers[index]) return;

        setActiveBottomButton(null); // Close any open tabs

        const newPlayer = teamPlayers[index];
        const newPlayerId = newPlayer.character_sheet_id || newPlayer.id;

        sessionStorage.setItem('selectedPlayerId', newPlayerId);

        setCurrentPlayerIndex(index);
        setPlayerData(newPlayer);
    };

    // Touch gesture handlers for swipe navigation
    const handleTouchStart = (e) => {
        // Only track touches when panel is DOWN (collapsed)
        // When panel is UP, we want native scrolling to work
        if (activeBottomButton !== null) {
            return; // Panel is up, don't track for player switching
        }
        
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        // Only track when panel is DOWN
        if (activeBottomButton !== null) {
            return;
        }
        
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        // Only process when panel is DOWN
        if (activeBottomButton !== null) {
            return;
        }
        
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;
        
        if (isLeftSwipe && currentPlayerIndex < teamPlayers.length - 1) {
            // Swipe left = go to next player (right)
            handlePlayerSwitch(currentPlayerIndex + 1);
        }
        
        if (isRightSwipe && currentPlayerIndex > 0) {
            // Swipe right = go to previous player (left)
            handlePlayerSwitch(currentPlayerIndex - 1);
        }
    };

    if (loading) {
        return (
            <div className="player-page-loading">
                <h2>Loading Player Data...</h2>
            </div>
        );
    }

    if (error) {
        return (
            <div className="player-page-error">
                <h2>Error: {error}</h2>
                <Button onClick={() => onNavigate('HOME')} variant="secondary">
                    Back to Team
                </Button>
            </div>
        );
    }

    if (!playerData) {
        return (
            <div className="player-page-error">
                <h2>No Player Data Available</h2>
                <Button onClick={() => onNavigate('HOME')} variant="secondary">
                    Back to Team
                </Button>
            </div>
        );
    }

    return (
        <div
            className="player-page-mobile"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Top Navigation Bar */}
            <div className="top-nav-bar">
                <Button
                    className="top-nav-button"
                    onClick={() => handleNavigation('LEAGUES')}
                    variant="secondary"
                >
                    LEAGUES
                </Button>
                <Button
                    className="top-nav-button"
                    onClick={() => handleNavigation('TEAM')}
                    variant="secondary"
                >
                    TEAM
                </Button>
                <Button
                    className="top-nav-button"
                    onClick={() => handleNavigation('STANDINGS')}
                    variant="secondary"
                >
                    STANDINGS
                </Button>
                <Button
                    className="top-nav-button"
                    onClick={() => handleNavigation('GEAR')}
                    variant="secondary"
                >
                    GEAR
                </Button>
            </div>

            {/* Player Navigation Dots */}
            {teamPlayers.length > 0 && (
                <div className="player-nav-dots">
                    {teamPlayers.map((player, index) => (
                        <button
                            key={player.id || player.character_sheet_id || index}
                            className={`player-dot ${index === currentPlayerIndex ? 'active' : ''}`}
                            onClick={() => handlePlayerSwitch(index)}
                            aria-label={`Switch to ${player.name}`}
                        />
                    ))}
                </div>
            )}

            {/* Player Name Display */}
            <div className="player-name-display">
                {playerData.name}
            </div>

            {/* Main Player Image Area */}
            <div className="player-image-area" style={{
                backgroundImage: 'url(/art/PlayerBG_Default.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
                <img
                    key={`${playerData.id || playerData.character_sheet_id}:${getPlayerImagePath(playerData)}`}
                    src={getPlayerImagePath(playerData)}
                    onError={(event) => handlePlayerImageError(event, playerData)}
                    alt={playerData.name}
                    className="player-main-image"
                />
            </div>

            {/* Sliding Panel - Contains tabs and content */}
            <div className={`sliding-panel ${(activeBottomButton === 'name' || activeBottomButton === 'pts' || activeBottomButton === 'gear') ? 'visible' : ''}`}>
                {/* Tabs at top of panel */}
                <div className="bottom-buttons">
                    <button
                        className={`bottom-button player-name-button ${activeBottomButton === 'name' ? 'active' : ''}`}
                        onClick={() => handleBottomButtonClick('name')}
                    >
                        DETAILS
                    </button>
                    <button
                        className={`bottom-button pts-button ${activeBottomButton === 'pts' ? 'active' : ''}`}
                        onClick={() => handleBottomButtonClick('pts')}
                    >
                        PTS
                    </button>
                    <button
                        className={`bottom-button gear-button ${activeBottomButton === 'gear' ? 'active' : ''}`}
                        onClick={() => handleBottomButtonClick('gear')}
                    >
                        GEAR
                    </button>
                </div>
                
                {/* Details Content - Shown when name tab is active */}
                {activeBottomButton === 'name' && (
                    <div className="details-content">
                        <ul className="details-list">
                            <li>YEARS IN THE LEAGUE</li>
                            <li>YEARS OF AGE</li>
                            <li>ZODIAC SIGN</li>
                            <li>COLLEGE ATTENDED</li>
                            <li>DRAFT POSITION</li>
                            <li>CURRENT FACIAL HAIR</li>
                            <li>
                                LAST WEEK'S TEAM RESULT
                                <ul className="sub-list">
                                    <li>WIN 24/21 @ PANTHERS</li>
                                </ul>
                            </li>
                            <li>
                                LAST WEEK'S STAT LINE
                                <ul className="sub-list">
                                    <li>PTS</li>
                                    <li>TDS</li>
                                    <li>PASS ATTEMPTS / COMPLETIONS</li>
                                    <li>PASSING YARDS</li>
                                    <li>CARRIES / RUSHING YARDS</li>
                                    <li>TARGETS / RECEPTIONS</li>
                                    <li>RECEIVING YARDS</li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                )}

                {/* Points Content - Shown when PTS tab is active */}
                {activeBottomButton === 'pts' && (
                    <div className="details-content points-content">
                        <CollapsibleScoreBreakdown
                            breakdown={playerData.detailedBreakdown}
                        />
                    </div>
                )}

                {/* Gear Content - Shown when GEAR tab is active.

                    PREVIOUSLY this was a read-only list whose only affordance was
                    deep-linking across to GearPage. It is now a full loadout editor
                    with a projected-score preview, so a manager can evaluate and
                    change a loadout without leaving the player they are looking at.

                    ⚠️ WRITES FAIL CLOSED — see the weekStatus state declaration.
                    Permission requires the status to have been READ (`weekStatusResolved`)
                    AND to say the week is not scoring. An earlier version used
                    `weekStatus ? !weekStatus.stats_active : true`, which treated a
                    missing status as permission; because AppRouter's playerPage route
                    does not pass the prop, that made this editor writable during
                    scoring on the production path. The weekly backend has NO
                    equivalent freeze to catch it — SeasonMutationGuard no-ops for
                    weekly leagues — so the loadout really could change mid-scoring. */}
                {activeBottomButton === 'gear' && (
                    <div className="details-content gear-content">
                        <PlayerGearProjectionTab
                            player={playerData}
                            teamGear={teamGear}
                            gearLoadError={gearLoadError}
                            /* ⭐ THE SAME OBJECT THE PROJ BAR BELOW RENDERS.
                               Passed down rather than recomputed in the tab, so
                               the two surfaces cannot show different numbers —
                               there is only one projection on this screen. */
                            projectionSummary={pageProjection}
                            projectionPending={projectionPending}
                            projectionError={projectionError}
                            canModifyGear={gearMutationsPermitted}
                            weekStatusKnown={weekStatusResolved}
                            onEquip={handleEquipGear}
                            onUnequip={handleUnequipGear}
                            isOpponentView={isOpponentView}
                        />
                    </div>
                )}
            </div>

            {/* Score Bar - Separate fixed element, always visible at bottom.

                PROJ READS FROM THE SAME ADAPTER AS THE GEAR TAB — see
                `pageProjection` above. It previously rendered
                `playerData.projected_points` (NFLProjectionMirror), so this bar
                could read `PROJ: 18.7` while the gear tab, two inches above it,
                said `Base: 13.65` from the historical tier averages. Two
                providers, one screen, no way for a user to know which was real.

                The value shown is the projected TOTAL — base estimate plus the
                equipped gear's baked impact — because that is what the player
                is actually expected to score with their current loadout. It
                therefore moves when gear is equipped or removed, which is the
                whole point of the feature.

                Unknown renders as an em dash via `formatPoints()`, never as
                `0`. A fabricated zero next to a real SCORE would read as a
                confident prediction of nothing. */}
            <div className="score-bar-separate">
                <div className="score-item-separate">
                    <span className="score-label-separate">SCORE:</span>
                    <span className="score-value-separate current-score">
                        {playerData.score || 0}
                    </span>
                </div>
                <div className="score-item-separate">
                    <span className="score-label-separate">PROJ:</span>
                    <span
                        className="score-value-separate proj-score"
                        title={
                            // `pageProjection` is null in THREE different
                            // situations, and a bare em dash cannot tell them
                            // apart — so the tooltip names which one it is.
                            // "We could not read it" must never hover with the
                            // same words as "this player has no tier data".
                            gearLoadError
                                ? 'Projection unavailable — your gear inventory ' +
                                  'could not be read, so this player\u2019s loadout ' +
                                  'is unknown.'
                                : projectionError
                                ? 'Projection could not be loaded. This is a ' +
                                  'failure to read it, not a player without a ' +
                                  'projection — reload to try again.'
                                : projectionPending
                                ? 'Loading projection\u2026'
                                : !pageProjection?.available
                                ? pageProjection?.reason
                                : pageProjection.totalIsPartial
                                ? 'Partial projected points for ' +
                                  `${pageProjection.source?.year} week ${pageProjection.source?.week}. ` +
                                  (pageProjection.partialScoringInputs?.length
                                      ? 'Tank01 generic two-point conversions cannot be attributed; their points are excluded. '
                                      : '') +
                                  (pageProjection.unknownGearCount > 0
                                      ? `${pageProjection.unknownGearCount} equipped item` +
                                        `${pageProjection.unknownGearCount === 1 ? ' has' : 's have'} ` +
                                        'no resolvable scoring definition and is not included, so this total is partial.'
                                      : '')
                                : 'Projected from real NFL player stats for ' +
                                  `${pageProjection.source?.year} week ${pageProjection.source?.week}, ` +
                                  'including equipped gear and team-context scoring'
                        }
                    >
                        {formatPoints(pageProjection?.totalPoints ?? null)
                            .replace('pts', '')}
                        {/* ⚠️ MARK A PARTIAL TOTAL — DO NOT PRESENT IT AS WHOLE.
                            When some equipped items have no projection data,
                            `sumGearProjection()` excludes them, so this figure
                            is real but UNDERSTATES the loadout. Rendered bare it
                            is indistinguishable from a complete projection, and
                            the always-visible bar has no room for the sentence
                            the gear tab shows. The asterisk is the minimum
                            honest signal, and the tooltip above carries the
                            detail. */}
                        {pageProjection?.available
                            && pageProjection.totalIsPartial
                            && <span className="proj-partial-marker">*</span>}
                    </span>
                </div>
            </div>

        </div>
    );
}

export default PlayerPage;
